import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Building, Calendar, TrendingUp, Filter, X, Cog, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { projects } from '../mock/mockData';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';

const StatusBadge = ({ status }) => (
  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold ${
      status === 'Under Installation' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'
    }`}
    data-testid="project-status-badge"
  >
    {status}
  </span>
);

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'mineral-water', label: 'Mineral Water' },
    { id: 'juice', label: 'Juice Processing' },
    { id: 'soft-drink', label: 'Soft Drinks' }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Project Case Studies | Mineral Water Plant Installations Across India | Alden Technologies"
        description="Recent turnkey water bottling plant case studies — 24 to 120 BPM installations across India with capacity, location, technology and project status. 200+ plants delivered."
        path="/projects"
      />
      <Breadcrumbs items={[{ label: 'Project Case Studies' }]} />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1774887810190-5de1cc9cb06e"
            alt="Our Projects"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Project Case Studies
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Recent turnkey plant installations across India — capacity, location, technology and project status
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? "default" : "outline"}
                className={selectedCategory === category.id 
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                  : "border-gray-300 hover:border-emerald-600 hover:text-emerald-600"
                }
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="border-2 border-gray-200 hover:border-emerald-500 hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={`${project.title} - ${project.capacity} turnkey plant in ${project.location}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <StatusBadge status={project.status} />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {project.year}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{project.title}</h3>
                  
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm">{project.client}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm">{project.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium">{project.capacity}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={() => setSelectedProject(project)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    View Details
                    <ArrowRight className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProject(null)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-96">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {selectedProject.year}
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium capitalize">
                    {selectedProject.category.replace('-', ' ')}
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-white mb-2">{selectedProject.title}</h2>
                <p className="text-emerald-300 text-xl font-medium">{selectedProject.client}</p>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-emerald-50 p-6 rounded-lg">
                  <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Location</h4>
                  <p className="text-lg font-bold text-gray-900">{selectedProject.location}</p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-emerald-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Capacity</h4>
                  <p className="text-lg font-bold text-gray-900">{selectedProject.capacity}</p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-lg">
                  <Calendar className="w-8 h-8 text-emerald-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Year</h4>
                  <p className="text-lg font-bold text-gray-900">{selectedProject.year}</p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-lg">
                  <Activity className="w-8 h-8 text-emerald-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Project Status</h4>
                  <p className="text-lg font-bold text-gray-900">{selectedProject.status}</p>
                </div>
              </div>

              <div className="bg-emerald-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <Cog className="w-8 h-8 text-emerald-600 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Technology &amp; Machinery Supplied</h4>
                    <p className="text-lg font-bold text-gray-900">{selectedProject.technology}</p>
                    <p className="text-sm text-emerald-700 font-semibold mt-2">Scope: {selectedProject.scope}</p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Project Overview</h3>
                <p className="text-lg text-gray-700 leading-relaxed">{selectedProject.description}</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Highlights</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Complete turnkey project execution from design to commissioning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">PLC-based automation for efficient plant operations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Comprehensive operator training and documentation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Ongoing technical support and maintenance services</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-4">
                <Link to="/contact" className="flex-1">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg">
                    Start Your Project
                  </Button>
                </Link>
                <Button 
                  onClick={() => setSelectedProject(null)}
                  variant="outline"
                  className="flex-1 py-6 text-lg border-2"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section */}
      <section className="py-20 bg-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12">
            Our Project Success
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div>
              <div className="text-5xl font-bold text-white mb-2">200+</div>
              <div className="text-emerald-100">Plants Installed</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">150+</div>
              <div className="text-emerald-100">Happy Clients</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">5+</div>
              <div className="text-emerald-100">States Covered</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">15+</div>
              <div className="text-emerald-100">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Start Your <span className="text-emerald-600">Project?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join our list of successful clients with a world-class beverage plant
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg">
              Get Started Today
              <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <InternalLinks currentPath="/projects" />
    </div>
  );
};

export default Projects;
